import React, { useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import isAuthenticated from '../../../redux/auth/authUtils';
import { Feature } from '../../../redux/feature-toggles/types';
import {
  closeFeedbackForm,
  fetchFeedbackSurveyQuestions,
  openFeedbackForm,
  saveFeedback,
} from '../../../redux/feedback/feedback-slice';
import { useAppDispatch } from '../../../redux/store';
import { RootState } from '../../../redux/types';
import { Survey } from '../../../types/instant-lease-api';
import Modal from '../../b2b/components/modal/modal';
import RadioContainer from '../../b2b/components/radio-container/radio-container';
import Radio from '../../b2b/components/radio/radio';
import ButtonGroup from '../../b2b/molecules/button-group/button-group';
import './feedback.css';
import t from './feedback.json';

const tq = t.questions as {
  [key: string]: {
    title: string;
    description: string;
    options: { [key: string]: string };
  };
};

interface QuestionProps {
  question: Survey.Question;
  index: number;
  form: UseFormReturn<Survey.Answer[]>;
}

const QuestionContainer: React.FC<QuestionProps> = ({
  question,
  index,
  form: {
    watch,
    setValue,
    register,
    formState: { errors },
  },
}) => (
  <RadioContainer
    title={tq[question.name].title}
    subTitle={tq[question.name].description}
  >
    {question.options.map(({ key }) => (
      <Radio
        {...register(`${index}.answer`, { required: 'Pflichtfeld' })}
        value={watch(`${index}.answer`) || ''}
        checked={watch(`${index}.answer`) === key}
        twolines={true}
        onChange={() => {
          setValue(`${index}.answer`, key);
        }}
        className='content-normal'
        key={key}
      >
        {tq[question.name].options[key]}
      </Radio>
    ))}
    {errors[index]?.answer?.message && (
      <p className='error small-text left'>{errors[index]?.answer?.message}</p>
    )}
  </RadioContainer>
);

const FeedbackContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const { feedbackForm, questions } = useSelector(
    (state: RootState) => state.feedback,
  );
  const { [Feature.Feedback]: feedback } = useSelector(
    (state: RootState) => state.featureToggles,
  );

  const [loading, setLoading] = useState<boolean>(false);

  const form = useForm<Survey.Answer[]>({
    mode: 'all',
    reValidateMode: 'onChange',
    values:
      questions?.map((q) => ({ answer: '', question_name: q.name })) || [],
  });

  useEffect(() => {
    isAuthenticated(auth) &&
      !questions &&
      dispatch(fetchFeedbackSurveyQuestions({ accessToken: auth.accessToken }));
  }, [questions, auth]);

  const close = () => dispatch(closeFeedbackForm());

  const submitData = (answers: Survey.Answer[]) => {
    setLoading(true);
    dispatch(
      saveFeedback({
        accessToken: auth.accessToken,
        answer_list: answers,
      }),
    ).then(() => {
      setLoading(false);
      close();
    });
  };

  return questions && feedback.enabled && isAuthenticated(auth) ? (
    <div className='feedback'>
      <ButtonGroup
        className='feedback-opener'
        type='default'
        buttonOneProps={{
          theme: 'tertiary',
          type: 'button',
          children: 'Feedback',
          onClick: () => dispatch(openFeedbackForm()),
        }}
      />

      <Modal
        isOpen={feedbackForm.isOpened}
        onClose={close}
        type='middle'
        direction='up'
        background='transparent'
      >
        <h1 className='title'>{t.title}</h1>
        <div className='feedback-container'>
          <form onSubmit={form.handleSubmit(submitData)}>
            <p className='content-normal dark-grey'>{t.description}</p>
            {questions?.map((q, i) => (
              <section key={q.id}>
                {!!q.options.length && (
                  <QuestionContainer form={form} question={q} index={i} />
                )}
                {!q.options.length && (
                  <>
                    <h3 className='content-bold black'>{tq[q.name].title}</h3>
                    <p className='content-normal'>{tq[q.name].description}</p>
                    <textarea
                      className='content-normal'
                      rows={5}
                      {...form.register(`${i}.answer`)}
                    />
                  </>
                )}
              </section>
            ))}
            <ButtonGroup
              className='back-next'
              type='back-next'
              buttonOneProps={{
                theme: 'primary',
                type: 'submit',
                loading,
                children: 'Feedback abschicken',
              }}
            />
          </form>
        </div>
      </Modal>
    </div>
  ) : null;
};

export default FeedbackContainer;
